import {
  FormControlLabel,
  Grid,
  Skeleton,
  Switch,
  Typography,
} from "@mui/material";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { OrganizationUserContext } from "contexts/Organization";
import { forwardRef, useContext } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BentoBrand, SearchParams, UserRequestSource } from "schemas/dashboard";
import { routes } from "schemas/routes";

import { useSearchBrand } from "utils/useSearchBrand";

import DiscoverCard from "../DiscoverCard";
import styles from "./styles";

interface Props {
  brandsLoading: boolean;
  fetchMoreLoading: boolean;
  brands: BentoBrand[];
  ref: any;
  totalCount: number | null;
}
const REF_INDEX = 9; // The index used to start loading more recommendations

export default forwardRef<HTMLDivElement, Props>(
  ({ brandsLoading, fetchMoreLoading, brands, totalCount }, ref) => {
    const [searchParams] = useSearchParams();
    const { discoverTab } = useContext(DiscoverViewContext);

    const { handleRequestForBrand } = useSearchBrand();

    const brandQuery = searchParams.get(SearchParams.QUERY_DISCOVER);

    const isSearching = brandQuery && brandQuery?.length > 0;
    const { excludedBentoSearchFilterIds, searchFilters } = useContext(
      OrganizationUserContext,
    );

    const isExcludedKeyWords = () => {
      if (!excludedBentoSearchFilterIds) return false;
      for (const excludedFilterId of excludedBentoSearchFilterIds) {
        const matchedFilter = searchFilters?.find(
          (f) => f.id === excludedFilterId,
        );
        if (matchedFilter) {
          const inMatchedFilter = matchedFilter?.keywords?.find(
            (x) => x === brandQuery?.toLowerCase(),
          );
          if (inMatchedFilter) return true;
        }
      }
      return false;
    };

    return (
      <Grid item container rowGap={1}>
        <Grid item xs={12}>
          <Typography sx={styles.searchingText} variant="h6" textAlign="left">
            {isSearching && `Searching "${brandQuery}"`}
          </Typography>
        </Grid>

        {!brandsLoading && !fetchMoreLoading && brands.length === 0 && (
          <Grid container item xs={12} rowGap={2}>
            {isExcludedKeyWords() ? (
              <Grid sx={styles.excludedContainer}>
                <Typography sx={{ fontSize: 16, mb: 2 }}>
                  No results found.
                </Typography>
                It appears you have excluded the keyword{" "}
                <strong>
                  <em>{brandQuery}</em>
                </strong>{" "}
                from your recommendation preferences. To change this, go to your{" "}
                <Link to={`/${routes.profile}`}>Profile page</Link> and update
                the excluded keyword filters at the{" "}
                <strong>Let us tailor your recommendations</strong> section.
              </Grid>
            ) : (
              <>
                <Grid item xs={12} sx={{ textAlign: "center" }}>
                  No results found {isExcludedKeyWords() && "True"}
                  {isSearching && (
                    <FormControlLabel
                      sx={{ mx: 1 }}
                      control={
                        <Switch
                          size="small"
                          onChange={() =>
                            handleRequestForBrand(
                              searchParams.get("queryBrand") || "",
                              UserRequestSource.SOURCE_TOGGLE,
                            )
                          }
                        />
                      }
                      label={
                        <Typography sx={styles.notifyText} component="span">
                          Notify me when results for{" "}
                          <strong>"{searchParams.get("queryBrand")}"</strong>{" "}
                          are found.
                        </Typography>
                      }
                    />
                  )}
                </Grid>
                <Grid item xs={12} sx={{ textAlign: "center" }}>
                  Try changing the filters above to see more results.
                </Grid>
              </>
            )}
          </Grid>
        )}

        {brandsLoading && (
          <>
            <Grid container item xs={12} justifyContent="flex-end">
              <Typography color="textSecondary" variant="caption">
                <Skeleton component="span" width={100} />
              </Typography>
            </Grid>
            <Grid container item xs={12} rowGap={1} columnSpacing={1}>
              {Array.from({ length: 20 }, (x, i) => (
                <Grid
                  item
                  xs={12}
                  md={12}
                  lg={discoverTab === null ? 6 : 12}
                  xl={6}
                  key={i}
                >
                  <Skeleton
                    key={i}
                    variant="rounded"
                    width={"100%"}
                    height={254}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {!brandsLoading && totalCount && (
          <Grid container item xs={12} justifyContent="flex-end">
            <Typography color="textSecondary" variant="caption">
              {totalCount === 10000 ? "10,000+" : totalCount} results
            </Typography>
          </Grid>
        )}

        <Grid container item xs={12} rowGap={1} columnSpacing={1}>
          {!brandsLoading &&
            brands?.map((brand: BentoBrand, idx: number) => (
              <Grid
                item
                xs={12}
                md={12}
                lg={discoverTab === null ? 6 : 12}
                xl={6}
                key={`brand-card-${brand.id}-${idx}`}
              >
                <DiscoverCard
                  ref={
                    (
                      brands.length > REF_INDEX
                        ? idx === brands.length - REF_INDEX
                        : idx === brands.length - 1
                    )
                      ? ref
                      : undefined
                  }
                  brand={brand}
                />
              </Grid>
            ))}
        </Grid>

        {!brandsLoading && fetchMoreLoading && (
          <Grid container item xs={12} rowGap={1} columnSpacing={1}>
            {Array.from({ length: 4 }, (x, i) => (
              <Grid
                item
                xs={12}
                md={12}
                lg={discoverTab === null ? 6 : 12}
                xl={6}
                key={i}
              >
                <Skeleton
                  key={i}
                  variant="rounded"
                  width={"100%"}
                  height={254}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Grid>
    );
  },
);
